import * as React from "react";
import { useLocation, useRoute } from "wouter";
import { ArrowLeft, MapPin, Package, ChevronRight, ChevronDown, FlaskConical, CheckCircle2, Pencil, Check, Sparkles, RefreshCw, Clock, X, Camera, Zap, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { useAuth } from "@/contexts/AuthContext.js";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext.js";
import { usePlan } from "@/contexts/PlanContext.js";
import { SPACE_ACTIVITIES, spaceStates, resolveSpaceActivities, hasUserDefinedActivities, spaceHasIntent } from "@homethyst/shared/schemas";
import { ReorganizeContainersPanel } from "./ReorganizeContainersPanel.js";
import { ReorganizeEntry } from "@/features/organize/ReorganizeEntry.js";

interface SpaceItem {
  id: string;
  name: string;
  spaceId: string | null;
}

interface SpaceDetail {
  id: string;
  name: string;
  type: string;
  roomType: string | null;
  purpose: string | null;
  activities: string[] | null;
  currentState: string | null;
  desiredState: string | null;
}

interface AdoptedArrangement {
  trialId: string;
  goalId: string | null;
  goalTitle: string;
  decidedAt: string | null;
  items: { itemId: string; itemName: string }[];
}

interface SpaceRoutine {
  id: string;
  title: string;
  frequency: string;
  nextDueDate: string;
  tasks: { id: string; label: string; isCompleted: boolean }[];
  trialStatus: string;
}

const STATE_LABELS: Record<string, { label: string; emoji: string }> = {
  organized: { label: "Organized", emoji: "✨" },
  somewhat_cluttered: { label: "Somewhat cluttered", emoji: "📦" },
  cluttered: { label: "Cluttered", emoji: "🗃️" },
  overwhelmed: { label: "Overwhelmed", emoji: "😰" },
  empty: { label: "Empty", emoji: "🫙" },
  in_progress: { label: "In progress", emoji: "🔧" },
};

function SpaceIntentPanel({ space, onUpdate }: { space: SpaceDetail; onUpdate: (updated: SpaceDetail) => void }) {
  const toast = useToast();
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  // Weak, room-type defaults resolved at read time. These are perceived more
  // softly than a user's own choice and are never persisted unless the user saves.
  const resolvedActivities = resolveSpaceActivities(space);
  const userHasActivities = hasUserDefinedActivities(space);
  const defaultActivityValues = userHasActivities ? [] : resolvedActivities.map((a) => a.value);

  const [purpose, setPurpose] = React.useState(space.purpose ?? "");
  // In edit mode, seed from the user's own activities, falling back to the weak
  // default suggestion so the user starts from it and can confirm or override.
  const [activities, setActivities] = React.useState<string[]>(
    space.activities && space.activities.length > 0 ? space.activities : defaultActivityValues
  );
  const [currentState, setCurrentState] = React.useState<string>(space.currentState ?? "");
  const [desiredState, setDesiredState] = React.useState<string>(space.desiredState ?? "");

  const roomKey = (space.roomType ?? space.name ?? "").toLowerCase().replace(/\s*\d+$/, "");
  const availableActivities = SPACE_ACTIVITIES[roomKey] ?? SPACE_ACTIVITIES._default;

  // "Has intent" reflects only what the user has explicitly set — a room-type
  // default alone does NOT count as the user having defined intent.
  const hasAnyIntent = space.purpose || userHasActivities || space.currentState || space.desiredState;
  const hasDefaultSuggestion = !userHasActivities && defaultActivityValues.length > 0;

  const toggleActivity = (value: string) => {
    setActivities((prev) => prev.includes(value) ? prev.filter((a) => a !== value) : [...prev, value]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await apiFetch<SpaceDetail>(`/api/spaces/${space.id}`, {
        method: "PUT",
        body: JSON.stringify({
          purpose: purpose || null,
          activities: activities.length > 0 ? activities : null,
          currentState: currentState || null,
          desiredState: desiredState || null,
        }),
      });
      onUpdate(updated);
      setEditing(false);
      toast.show("Space intent saved!");
    } catch (e: any) {
      toast.show(e.message ?? "Failed to save", "error");
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setPurpose(space.purpose ?? "");
    setActivities(space.activities ?? []);
    setCurrentState(space.currentState ?? "");
    setDesiredState(space.desiredState ?? "");
    setEditing(false);
  };

  // Show empty-state CTA if no intent has been set
  if (!hasAnyIntent && !editing) {
    return (
      <div className="bg-amethyst-soft rounded-lg border border-clay-border p-6 text-center animate-[fadeIn_0.5s_ease-in]">
        <div className="w-12 h-12 rounded-md bg-white flex items-center justify-center mx-auto mb-3">
          <Sparkles className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-[1.0625rem] font-semibold text-amethyst-ink mb-1">What's this space for?</h3>
        <p className="text-sm text-body mb-4 max-w-sm mx-auto leading-relaxed">
          Define how you use this space — what activities happen here and how you want it to feel.
          This unlocks personalized routines and organization suggestions tailored to your life.
        </p>
        {hasDefaultSuggestion && (
          <div className="mb-4">
            <p className="text-[0.8125rem] text-faint mb-1.5">Suggested for this room</p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {resolvedActivities.map((act) => {
                const actLabel = availableActivities.find((a) => a.value === act.value)?.label ?? act.value;
                return (
                  <span key={act.value} className="px-2.5 py-1 rounded-pill bg-white text-body text-[0.8125rem] font-medium border border-dashed border-clay-border">
                    {actLabel}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        <button onClick={() => setEditing(true)} className="px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">
          {hasDefaultSuggestion ? "Confirm or customize" : "Set up space intent"}
        </button>
        <p className="text-[0.8125rem] text-faint mt-3">Takes less than 30 seconds</p>
      </div>
    );
  }

  // Editing mode
  if (editing) {
    return (
      <div className="bg-white rounded-lg border border-clay-border p-5 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[1.0625rem] text-ink flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />Space Intent
          </h3>
        </div>

        {/* Purpose one-liner */}
        <div>
          <label className="text-sm font-medium text-body mb-1.5 block">What's this space for?</label>
          <input
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="e.g. Daily cooking and family meals"
            className="w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none"
            maxLength={200}
          />
        </div>

        {/* Activities multi-select */}
        <div>
          <label className="text-sm font-medium text-body mb-2 block">
            Activities that happen here
            {hasDefaultSuggestion && (
              <span className="ml-1.5 font-normal text-faint">· we pre-selected a suggestion — adjust as you like</span>
            )}
          </label>
          <div className="flex flex-wrap gap-2">
            {availableActivities.map((act) => (
              <button
                key={act.value}
                onClick={() => toggleActivity(act.value)}
                className={cn(
                  "px-3 py-1.5 rounded-pill text-sm font-medium transition-all",
                  activities.includes(act.value)
                    ? "bg-amethyst-soft text-amethyst-ink border border-primary/30"
                    : "bg-clay-muted text-body border border-clay-border hover:border-primary/40"
                )}
              >
                {act.label}
              </button>
            ))}
          </div>
        </div>

        {/* Current state vs. desired state */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-body mb-2 block">How is it now?</label>
            <div className="space-y-1.5">
              {spaceStates.map((state) => (
                <button
                  key={state}
                  onClick={() => setCurrentState(currentState === state ? "" : state)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-all",
                    currentState === state
                      ? "bg-warning-wash border border-clay-border text-warning-text"
                      : "bg-paper-raised border border-transparent hover:border-clay-border text-body"
                  )}
                >
                  <span>{STATE_LABELS[state].emoji}</span>
                  <span>{STATE_LABELS[state].label}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-body mb-2 block">How do you want it to feel?</label>
            <div className="space-y-1.5">
              {spaceStates.filter((s) => s !== "overwhelmed" && s !== "cluttered").map((state) => (
                <button
                  key={state}
                  onClick={() => setDesiredState(desiredState === state ? "" : state)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-all",
                    desiredState === state
                      ? "bg-sage-wash border border-clay-border text-sage-deep"
                      : "bg-paper-raised border border-transparent hover:border-clay-border text-body"
                  )}
                >
                  <span>{STATE_LABELS[state].emoji}</span>
                  <span>{STATE_LABELS[state].label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button onClick={handleCancel} className="px-4 py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium hover:bg-amethyst-soft/70 transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    );
  }

  // Display mode (intent has been set)
  return (
    <div className="bg-white rounded-lg border border-clay-border p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-ink flex items-center gap-2 text-sm">
          <Sparkles className="w-4 h-4 text-primary" />Space Intent
        </h3>
        <button onClick={() => setEditing(true)} aria-label="Edit space intent" className="p-1.5 rounded-md hover:bg-amethyst-soft transition-colors">
          <Pencil className="w-3.5 h-3.5 text-faint" />
        </button>
      </div>

      {space.purpose && (
        <p className="text-sm text-body italic">"{space.purpose}"</p>
      )}

      {userHasActivities ? (
        <div className="flex flex-wrap gap-1.5">
          {space.activities!.map((act) => {
            const actLabel = availableActivities.find((a) => a.value === act)?.label ?? act;
            return (
              <span key={act} className="px-2.5 py-1 rounded-pill bg-amethyst-soft text-amethyst-ink text-[0.8125rem] font-medium">
                {actLabel}
              </span>
            );
          })}
        </div>
      ) : hasDefaultSuggestion ? (
        // Weak default: rendered muted + dashed so it clearly reads as a suggestion,
        // not a deliberate user choice.
        <div>
          <div className="flex flex-wrap gap-1.5 items-center">
            {resolvedActivities.map((act) => {
              const actLabel = availableActivities.find((a) => a.value === act.value)?.label ?? act.value;
              return (
                <span key={act.value} className="px-2.5 py-1 rounded-pill bg-paper-raised text-body text-[0.8125rem] font-medium border border-dashed border-clay-border">
                  {actLabel}
                </span>
              );
            })}
            <span className="text-[0.8125rem] text-faint italic">suggested for this room</span>
          </div>
        </div>
      ) : null}

      {(space.currentState || space.desiredState) && (
        <div className="flex items-center gap-3 text-[0.8125rem] text-body">
          {space.currentState && (
            <span className="flex items-center gap-1">
              <span className="text-warning-text font-medium">Now:</span>
              <span>{STATE_LABELS[space.currentState]?.emoji} {STATE_LABELS[space.currentState]?.label}</span>
            </span>
          )}
          {space.currentState && space.desiredState && <span className="text-faint">→</span>}
          {space.desiredState && (
            <span className="flex items-center gap-1">
              <span className="text-sage-deep font-medium">Goal:</span>
              <span>{STATE_LABELS[space.desiredState]?.emoji} {STATE_LABELS[space.desiredState]?.label}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

interface RoutineSuggestionData {
  title: string;
  description: string;
  frequency: string;
  estimatedMinutes: number;
  tasks: { id: string; label: string }[];
}

function RoutineSuggestionCard({ spaceId, spaceName, itemCount, hasIntent }: { spaceId: string; spaceName: string; itemCount: number; hasIntent: boolean }) {
  const toast = useToast();
  const { user } = useAuth();
  const { aiEnabled } = useFeatureFlags();
  const { usage } = usePlan();
  const aiQuotaExhausted = usage && usage.aiActionsLimit !== -1 && usage.aiActionsUsed >= usage.aiActionsLimit;
  const [suggestions, setSuggestions] = React.useState<RoutineSuggestionData[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [dismissed, setDismissed] = React.useState(() => localStorage.getItem(`ht_routine_dismissed_${user?.id}_${spaceId}`) === "1");
  const [creating, setCreating] = React.useState<string | null>(null);
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiRequested, setAiRequested] = React.useState(false);

  React.useEffect(() => {
    if (dismissed || !hasIntent) { setLoading(false); return; }
    apiFetch<{ suggestions: RoutineSuggestionData[] }>(`/api/spaces/${spaceId}/routine-suggestions`)
      .then((data) => setSuggestions(data.suggestions))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [spaceId, dismissed, hasIntent]);

  const handleGetAISuggestions = async () => {
    setAiLoading(true);
    try {
      const { suggestions: aiSuggestions } = await apiFetch<{ suggestions: { title: string; description: string; frequency: string; estimatedMinutes: number; tasks: string[] }[] }>(
        "/api/ai/suggest-flows",
        { method: "POST", body: JSON.stringify({ forSpaceId: spaceId }) }
      );
      // Convert AI suggestions to match the RoutineSuggestionData format (tasks need ids)
      const formatted: RoutineSuggestionData[] = aiSuggestions.map((s) => ({
        title: s.title,
        description: s.description,
        frequency: s.frequency,
        estimatedMinutes: s.estimatedMinutes,
        tasks: s.tasks.map((label, i) => ({ id: `ai-${i}`, label })),
      }));
      setSuggestions((prev) => {
        const existingTitles = new Set(prev.map((p) => p.title.toLowerCase()));
        const newOnes = formatted.filter((f) => !existingTitles.has(f.title.toLowerCase()));
        return [...prev, ...newOnes];
      });
      setAiRequested(true);
    } catch (e: any) {
      toast.show(e.message?.includes("limit") ? "AI action limit reached" : "Failed to get AI suggestions", "error");
    }
    setAiLoading(false);
  };

  const handleAccept = async (suggestion: RoutineSuggestionData) => {
    setCreating(suggestion.title);
    try {
      const nextDue = new Date();
      const daysOffset = suggestion.frequency === "daily" ? 1
        : suggestion.frequency === "weekly" ? 7
        : suggestion.frequency === "quarterly" ? 90
        : 30; // monthly (default)
      nextDue.setDate(nextDue.getDate() + daysOffset);
      await apiFetch("/api/guided-flows", {
        method: "POST",
        body: JSON.stringify({
          title: suggestion.title,
          description: suggestion.description,
          frequency: suggestion.frequency,
          spaceId,
          nextDueDate: nextDue.toISOString(),
          estimatedMinutes: suggestion.estimatedMinutes,
          tasks: suggestion.tasks.map((t) => ({ ...t, isCompleted: false })),
        }),
      });
      toast.show(`"${suggestion.title}" routine created! Find it in Workflows.`);
      setSuggestions((prev) => prev.filter((s) => s.title !== suggestion.title));
    } catch (e: any) {
      toast.show(e.message ?? "Failed to create routine", "error");
    }
    setCreating(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(`ht_routine_dismissed_${user?.id}_${spaceId}`, "1");
    setDismissed(true);
  };

  // Don't show if: dismissed, loading, or no intent set
  // Show when hasIntent (even with 0 suggestions, to offer AI button — unless quota exhausted)
  if (dismissed || loading || !hasIntent) return null;
  if (suggestions.length === 0 && !(aiEnabled && !aiRequested && !aiQuotaExhausted)) return null;

  return (
    <div className="bg-sage-wash rounded-lg border border-clay-border p-5 space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-white flex items-center justify-center">
            <RefreshCw className="w-4 h-4 text-sage-deep" />
          </div>
          <div>
            <h3 className="text-[1.0625rem] font-semibold text-ink">Keep {spaceName} organized</h3>
            <p className="text-[0.8125rem] text-body tabular-nums">{hasIntent ? "Based on your intent for this space" : `You've mapped ${itemCount} items here`} — want a routine to maintain it?</p>
          </div>
        </div>
        <button onClick={handleDismiss} aria-label="Dismiss" className="p-1 rounded-md hover:bg-white/60 text-faint hover:text-ink">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-2">
        {suggestions.map((suggestion) => (
          <div key={suggestion.title} className="bg-white rounded-md p-4 border border-clay-border">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-ink">{suggestion.title}</h4>
                <p className="text-[0.8125rem] text-faint mt-0.5">{suggestion.description}</p>
              </div>
              <button
                onClick={() => handleAccept(suggestion)}
                disabled={creating === suggestion.title}
                className="ml-3 px-3 py-1.5 rounded-md bg-sage-deep text-white text-[0.8125rem] font-medium shrink-0 disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed hover:bg-sage-deep-hover transition-colors"
              >
                {creating === suggestion.title ? "Creating..." : "Start this"}
              </button>
            </div>
            <div className="flex items-center gap-3 text-[0.8125rem] text-faint">
              <span className="flex items-center gap-1 tabular-nums">
                <Clock className="w-3 h-3" />{suggestion.estimatedMinutes} min
              </span>
              <span className="capitalize">{suggestion.frequency}</span>
              <span className="tabular-nums">{suggestion.tasks.length} steps</span>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[0.8125rem] text-faint text-center">
        Routines appear in your Workflows tab. You can customize them anytime.
      </p>

      {/* AI-powered suggestion button */}
      {aiEnabled && hasIntent && !aiRequested && !aiQuotaExhausted && (
        <button
          onClick={handleGetAISuggestions}
          disabled={aiLoading}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md border border-sage-deep/40 text-sage-deep text-sm font-medium hover:bg-white/60 transition-colors disabled:border-clay-border disabled:text-faint disabled:cursor-not-allowed"
        >
          {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {aiLoading ? "Getting personalized suggestions..." : "Get AI suggestions for this room"}
        </button>
      )}
    </div>
  );
}

export default function SpaceDetailPage() {
  const [, navigate] = useLocation();
  const toast = useToast();
  const [match, params] = useRoute("/spaces/:id");
  const spaceId = params?.id;

  const [space, setSpace] = React.useState<SpaceDetail | null>(null);
  const [items, setItems] = React.useState<SpaceItem[]>([]);
  const [routines, setRoutines] = React.useState<SpaceRoutine[]>([]);
  const [arrangements, setArrangements] = React.useState<AdoptedArrangement[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showArrangements, setShowArrangements] = React.useState(false);
  const [justSavedIntent, setJustSavedIntent] = React.useState(false);
  const [showReorganize, setShowReorganize] = React.useState(false);
  const { aiEnabled: aiFlagEnabled } = useFeatureFlags();

  const reloadItems = React.useCallback(() => {
    if (spaceId) apiFetch<SpaceItem[]>(`/api/items?spaceId=${spaceId}&limit=200`).then(setItems).catch(() => {});
  }, [spaceId]);

  React.useEffect(() => {
    if (!spaceId) return;
    Promise.all([
      apiFetch<any[]>("/api/spaces").then((spaces) => {
        const found = spaces.find((s: any) => s.id === spaceId);
        if (found) setSpace(found);
      }),
      apiFetch<SpaceItem[]>(`/api/items?spaceId=${spaceId}&limit=200`).then(setItems),
      apiFetch<SpaceRoutine[]>(`/api/guided-flows?spaceId=${spaceId}`).then(setRoutines),
      apiFetch<AdoptedArrangement[]>(`/api/spaces/${spaceId}/adopted-arrangements`).then(setArrangements),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [spaceId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!space) {
    return (
      <div className="bg-error-wash rounded-lg border border-clay-border text-center py-16 px-6">
        <p className="text-error-text">Space not found</p>
        <button onClick={() => navigate("/spaces")} className="mt-3 text-sm text-primary font-medium hover:underline">Back to spaces</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button onClick={() => navigate("/spaces")} className="flex items-center gap-1 text-sm text-faint hover:text-ink mb-3 py-2">
          <ArrowLeft className="w-4 h-4" />Back to spaces
        </button>
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-semibold text-ink font-headline tracking-[-0.01em]">{space.name}</h1>
          <span className="text-[0.8125rem] text-faint uppercase bg-clay-muted px-2 py-0.5 rounded-pill tracking-wide">{space.type}</span>
          <button
            onClick={() => navigate(`/inventory?action=scan&spaceId=${spaceId}`)}
            className="ml-auto px-3 py-1.5 rounded-md bg-sage-wash text-sage-deep text-[0.8125rem] font-medium flex items-center gap-1.5 hover:bg-sage-wash/70 transition-colors"
          >
            <Camera className="w-3.5 h-3.5" />Scan space
          </button>
        </div>
      </div>

      {/* Space Intent */}
      {(space.type === "room" || space.type === "container") && (
        <SpaceIntentPanel space={space} onUpdate={(updated) => {
          const hadIntent = !!(space.purpose || (space.activities && space.activities.length > 0));
          const hasIntentNow = !!(updated.purpose || (updated.activities && updated.activities.length > 0));
          setSpace(updated);
          if (!hadIntent && hasIntentNow) setJustSavedIntent(true);
        }} />
      )}

      {/* Next step prompt — shown once after first intent save */}
      {justSavedIntent && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-amethyst-soft border border-clay-border animate-[fadeIn_0.3s_ease-in]">
          <Sparkles className="w-4 h-4 text-primary shrink-0" />
          <p className="text-sm text-body flex-1">
            <span className="font-medium text-amethyst-ink">Nice!</span> Based on your intent, we've found routines that fit — check below.
          </p>
          <button onClick={() => setJustSavedIntent(false)} aria-label="Dismiss" className="text-faint hover:text-ink text-sm shrink-0">✕</button>
        </div>
      )}

      {/* Post-cataloging bridge: routine suggestions */}
      {(space.type === "room" || space.type === "container") && (
        // Routine suggestions surface when the space has effective intent — user
        // intent OR a weak room-type default (the API resolves defaults the same way).
        <RoutineSuggestionCard spaceId={space.id} spaceName={space.name} itemCount={items.length} hasIntent={spaceHasIntent(space)} />
      )}

      {/* Reorganize (container-type spaces only) */}
      {space.type === "container" && aiFlagEnabled && showReorganize && (
        <ReorganizeContainersPanel
          container={{ id: space.id, name: space.name, parentId: (space as any).parentId ?? null }}
          items={items}
          onClose={() => setShowReorganize(false)}
          onApplied={reloadItems}
        />
      )}

      {/* Items in this space */}
      <div className="bg-white rounded-lg border border-clay-border p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-[1.0625rem] text-ink flex items-center gap-2 tabular-nums">
            <Package className="w-4 h-4 text-faint" />Items ({items.length})
          </h2>
          <div className="flex items-center gap-3">
            {items.length > 0 && (
              <ReorganizeEntry spaceId={space.id} spaceName={space.name} />
            )}
            {space.type === "container" && aiFlagEnabled && items.length > 0 && (
              <button
                onClick={() => setShowReorganize((v) => !v)}
                className="text-sm text-sage-deep font-medium hover:text-sage-deep-hover py-2 flex items-center gap-1"
              >
                <Sparkles className="w-3.5 h-3.5" />Quick fit
              </button>
            )}
            <button
              onClick={() => navigate(`/inventory?action=add&spaceId=${spaceId}`)}
              className="text-sm text-primary font-medium hover:text-amethyst-ink py-2"
            >
              + Add item here
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="bg-sage-wash rounded-md text-center py-6 px-4"><p className="text-sm text-body">No items here yet — add your first to this space.</p></div>
        ) : (
          <div className="space-y-1">
            {items.slice(0, 20).map((item) => (
              <button
                key={item.id}
                onClick={() => navigate(`/inventory?highlight=${item.id}`)}
                className="w-full flex items-center justify-between py-2 px-3 rounded-md hover:bg-amethyst-soft text-left"
              >
                <span className="text-sm text-ink">{item.name}</span>
                <ChevronRight className="w-3.5 h-3.5 text-faint" />
              </button>
            ))}
            {items.length > 20 && (
              <button
                onClick={() => navigate(`/inventory?spaceId=${spaceId}`)}
                className="w-full text-center py-2 text-sm text-primary font-medium tabular-nums"
              >
                View all {items.length} items →
              </button>
            )}
          </div>
        )}
      </div>

      {/* Routines for this space */}
      {routines.length > 0 && (
        <div className="bg-white rounded-lg border border-clay-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-[1.0625rem] text-ink flex items-center gap-2 tabular-nums">
              <Zap className="w-4 h-4 text-faint" />Routines ({routines.length})
            </h2>
            <button
              onClick={() => navigate("/organize")}
              className="text-sm text-primary font-medium hover:text-amethyst-ink py-2"
            >
              View all →
            </button>
          </div>
          <div className="space-y-2">
            {routines.map((routine) => {
              const completedCount = routine.tasks.filter((t) => t.isCompleted).length;
              const totalTasks = routine.tasks.length;
              const progress = totalTasks > 0 ? completedCount / totalTasks : 0;
              const isOverdue = new Date(routine.nextDueDate) < new Date();
              return (
                <button
                  key={routine.id}
                  onClick={() => navigate("/organize")}
                  className="w-full text-left p-3 rounded-md border border-clay-border hover:border-primary/40 transition-colors"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-ink">{routine.title}</span>
                    <span className={cn("text-[0.8125rem] font-medium px-2 py-0.5 rounded-pill", isOverdue ? "bg-warning-wash text-warning-text" : "bg-clay-muted text-body")}>
                      {isOverdue ? "Overdue" : routine.frequency}
                    </span>
                  </div>
                  {totalTasks > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-clay-muted rounded-pill">
                        <div className="h-full bg-primary rounded-pill transition-all" style={{ width: `${progress * 100}%` }} />
                      </div>
                      <span className="text-[0.8125rem] text-faint tabular-nums">{completedCount}/{totalTasks}</span>
                    </div>
                  )}
                  {routine.trialStatus === "trial" && (
                    <span className="inline-block mt-1.5 text-[0.8125rem] text-amethyst-ink bg-amethyst-soft px-2 py-0.5 rounded-pill">Trial</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Adopted arrangements — subtle, seekable, below items */}
      {arrangements.length > 0 && (
        <div className="bg-white rounded-lg border border-clay-border p-4">
          <button
            onClick={() => setShowArrangements(!showArrangements)}
            className="w-full flex items-center justify-between py-1"
          >
            <span className="text-sm font-medium text-body flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-sage-deep" />
              Arranged on purpose
            </span>
            <ChevronDown className={`w-3.5 h-3.5 text-faint transition-transform ${showArrangements ? "rotate-180" : ""}`} />
          </button>

          {showArrangements && (
            <div className="mt-3 space-y-3">
              {arrangements.map((arr) => (
                <button
                  key={arr.trialId}
                  onClick={() => arr.goalId && navigate(`/organize/goals/${arr.goalId}`)}
                  className="w-full text-left p-3 rounded-md bg-sage-wash border border-clay-border hover:border-sage-deep/40 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FlaskConical className="w-3 h-3 text-sage-deep" />
                    <span className="text-sm font-medium text-sage-deep">{arr.goalTitle}</span>
                    {arr.decidedAt && (
                      <span className="text-[0.8125rem] text-sage-deep ml-auto tabular-nums">
                        kept {new Date(arr.decidedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-[0.8125rem] text-body pl-5">
                    {arr.items.map((i) => i.itemName).join(", ")}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
